import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

const RolesContext = createContext();

const ROLES_STORAGE_KEY = 'rolesMatrix';
const ROLES_STORAGE_TS_KEY = 'rolesMatrixFetchedAt';

const loadCachedRoles = () => {
  try {
    const cached = localStorage.getItem(ROLES_STORAGE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    return null;
  }
};

const loadCachedTimestamp = () => {
  try {
    return localStorage.getItem(ROLES_STORAGE_TS_KEY) || null;
  } catch (error) {
    return null;
  }
};

export const useRoles = () => {
  const context = useContext(RolesContext);
  if (!context) {
    throw new Error('useRoles must be used within a RolesProvider');
  }
  return context;
};

export const RolesProvider = ({ children }) => {
  const [roles, setRoles] = useState(loadCachedRoles);
  const [rolesFetchedAt, setRolesFetchedAt] = useState(loadCachedTimestamp);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshRoles = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/roles`);
      const data = response.data;
      setRoles(data);
      const fetchedAt = new Date().toISOString();
      setRolesFetchedAt(fetchedAt);
      localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(ROLES_STORAGE_TS_KEY, fetchedAt);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearRoles = useCallback(() => {
    setRoles(null);
    setRolesFetchedAt(null);
    setError(null);
    localStorage.removeItem(ROLES_STORAGE_KEY);
    localStorage.removeItem(ROLES_STORAGE_TS_KEY);
  }, []);

  const value = useMemo(() => ({
    roles,
    rolesFetchedAt,
    isLoading,
    error,
    refreshRoles,
    clearRoles
  }), [roles, rolesFetchedAt, isLoading, error, refreshRoles, clearRoles]);

  return (
    <RolesContext.Provider value={value}>
      {children}
    </RolesContext.Provider>
  );
};
